import { useEffect, useRef, type CSSProperties } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { env } from '@/env';
import { useT } from '@/i18n';

const TOKEN = env.mapboxToken;

// GeoJSON outer ring: [[lng, lat], …] (closed). Matches what the backend stores
// on DeliveryZone.polygon and what resolveZone's point-in-poly consumes.
export type PolygonRing = number[][];

const DEFAULT_CENTER: [number, number] = [13.405, 52.52];

// Distinct per-zone color via golden-angle hue spacing: each successive zone's
// hue jumps ~137.5°, so colors stay well-separated for ANY number of zones (no
// fixed-palette cap / repeat) and are stable per index (a zone keeps its color
// as others are added). Fixed saturation/lightness for contrast on a street map.
// Shared by the editor, the read-only preview, and the section list so a zone
// shows the SAME color everywhere.
export const zoneColor = (index: number) => `hsl(${Math.round((index * 137.508) % 360)}, 70%, 45%)`;

/**
 * Map each zone id to its `zoneColor`, indexed by the given order. Pass the
 * POLYGON zone ids sorted by displayOrder so a zone gets the SAME color in the
 * editor, the preview, and the list.
 */
export function zoneColorMap(orderedIds: string[]): Map<string, string> {
  const m = new Map<string, string>();
  orderedIds.forEach((id, i) => m.set(id, zoneColor(i)));
  return m;
}

/** A read-only reference area (another zone) shown beneath the one being edited. */
export interface ReferenceZone {
  id: string;
  label: string;
  ring: PolygonRing;
  /** Per-zone hex color (assigned by the parent so each zone is distinct). */
  color: string;
}

/**
 * A map with polygon-draw controls for a single delivery area. Drawing,
 * editing, or deleting the shape emits its outer ring (or null when cleared).
 * Restricted to one polygon - drawing a new one replaces the old. Any
 * `otherZones` are drawn as non-editable colored reference areas (with labels)
 * so the owner can see which zones are already covered.
 */
export function MapboxPolygonEditor({
  value,
  center,
  otherZones = [],
  onChange,
}: {
  value: PolygonRing | null;
  center?: { latitude: number; longitude: number } | null;
  otherZones?: ReferenceZone[];
  onChange: (ring: PolygonRing | null) => void;
}) {
  const pe = useT().orderSetup.polygonEditor;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = TOKEN;
    const start: [number, number] = center
      ? [center.longitude, center.latitude]
      : value && value.length
        ? (value[0] as [number, number])
        : DEFAULT_CENTER;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: start,
      zoom: 12,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
    });
    map.addControl(draw);

    const emit = () => {
      const poly = draw.getAll().features.find((f) => f.geometry.type === 'Polygon');
      const ring = poly ? (poly.geometry as GeoJSON.Polygon).coordinates[0] : null;
      onChangeRef.current((ring as PolygonRing) ?? null);
    };

    // Keep at most one polygon - a freshly drawn shape replaces the previous.
    map.on('draw.create', () => {
      const features = draw.getAll().features;
      if (features.length > 1) {
        const newest = features[features.length - 1];
        draw.delete(features.filter((f) => f.id !== newest.id).map((f) => String(f.id)));
      }
      emit();
    });
    map.on('draw.update', emit);
    map.on('draw.delete', emit);

    map.on('load', () => {
      const bounds = new mapboxgl.LngLatBounds();
      let hasBounds = false;
      const extendRing = (ring: PolygonRing) =>
        ring.forEach(([lng, lat]) => {
          bounds.extend([lng, lat]);
          hasBounds = true;
        });

      // Restaurant location pin - the origin the owner draws the zone AROUND.
      // A visible marker (vs. a bare map center) tells them where the shop sits
      // relative to the area they're outlining. Always kept in view below.
      if (center) {
        new mapboxgl.Marker({ color: '#ef4444' })
          .setLngLat([center.longitude, center.latitude])
          .addTo(map);
        bounds.extend([center.longitude, center.latitude]);
        hasBounds = true;
      }

      // Reference layer: other zones in amber, non-editable, with labels.
      const refFeatures = otherZones
        .filter((z) => z.ring && z.ring.length >= 3)
        .map((z) => ({
          type: 'Feature' as const,
          properties: { label: z.label, color: z.color },
          geometry: { type: 'Polygon' as const, coordinates: [z.ring] },
        }));
      if (refFeatures.length) {
        map.addSource('other-zones', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: refFeatures } as GeoJSON.FeatureCollection,
        });
        map.addLayer({
          id: 'other-zones-fill',
          type: 'fill',
          source: 'other-zones',
          paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.2 },
        });
        map.addLayer({
          id: 'other-zones-line',
          type: 'line',
          source: 'other-zones',
          paint: { 'line-color': ['get', 'color'], 'line-width': 2 },
        });
        map.addLayer({
          id: 'other-zones-label',
          type: 'symbol',
          source: 'other-zones',
          layout: { 'text-field': ['get', 'label'], 'text-size': 12 },
          paint: { 'text-color': '#1f2937', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
        });
        otherZones.forEach((z) => z.ring && z.ring.length >= 3 && extendRing(z.ring));
      }

      // The editable zone (blue, with vertex handles) sits on top.
      if (value && value.length >= 3) {
        draw.add({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [value] },
        });
        extendRing(value);
      }

      if (hasBounds) map.fitBounds(bounds, { padding: 40, duration: 0, maxZoom: 15 });
    });

    mapRef.current = map;
    drawRef.current = draw;
    return () => {
      map.remove();
      mapRef.current = null;
      drawRef.current = null;
    };
    // Init once; the parent owns the value via onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!TOKEN) {
    return (
      <div style={{ padding: 16, color: 'var(--f7-list-item-subtitle-text-color)' }}>
        {pe.unavailable}
      </div>
    );
  }

  // One legend row per zone, each in its own color (square = map area). The
  // restaurant pin leads when a location is set, so the round red swatch on the
  // map is identified.
  const legend: { key: string; swatch: CSSProperties; label: string }[] = [
    ...(center
      ? [
          {
            key: '__restaurant',
            label: pe.restaurant,
            swatch: {
              background: '#ef4444',
              border: '1.5px solid #b91c1c',
              borderRadius: '50%',
            } as CSSProperties,
          },
        ]
      : []),
    {
      key: '__editing',
      label: value ? pe.editingArea : pe.newArea,
      swatch: {
        background: 'rgba(59,178,208,0.3)',
        border: '1.5px solid #3bb2d0',
        borderRadius: 2,
      },
    },
    ...otherZones
      .filter((z) => z.ring && z.ring.length >= 3)
      .map((z) => ({
        key: z.id,
        label: z.label,
        swatch: {
          background: z.color,
          border: '1px solid rgba(0,0,0,0.2)',
          borderRadius: 2,
        } as CSSProperties,
      })),
  ];

  return (
    <div style={{ position: 'relative' }}>
      <div ref={containerRef} style={{ width: '100%', height: 360 }} />
      <div
        style={{
          position: 'absolute',
          left: 8,
          bottom: 8,
          background: 'rgba(255,255,255,0.92)',
          borderRadius: 8,
          padding: '6px 9px',
          fontSize: 11,
          lineHeight: 1.5,
          color: '#111',
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          maxHeight: 160,
          maxWidth: 200,
          overflowY: 'auto',
        }}
      >
        {legend.map((item) => (
          <div
            key={item.key}
            style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 12,
                height: 12,
                flexShrink: 0,
                ...item.swatch,
              }}
            />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Normalize a stored polygon (bare ring or `{ coordinates }`) to a ring. */
export function toPolygonRing(polygon: unknown): PolygonRing | null {
  if (Array.isArray(polygon)) return polygon as PolygonRing;
  const coords = (polygon as { coordinates?: unknown })?.coordinates;
  return Array.isArray(coords) ? (coords as PolygonRing) : null;
}
